<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page crm-reminder-page">
      <section class="reminder-overview">
        <div class="overview-copy">
          <span class="overview-kicker">我的跟进节奏</span>
          <h2>跟进待办</h2>
          <p>仅展示当前登录人名下、今天到期或已经逾期的线索与客户。</p>
        </div>
        <button class="metric metric-danger" type="button" :class="{ active: query.reminderStatus === 'overdue' }" @click="selectStatus('overdue')">
          <span>已逾期</span><b>{{ summary.overdueCount }}</b><small>需优先处理</small>
        </button>
        <button class="metric metric-warning" type="button" :class="{ active: query.reminderStatus === 'due_today' }" @click="selectStatus('due_today')">
          <span>今日到期</span><b>{{ summary.dueTodayCount }}</b><small>今日完成跟进</small>
        </button>
        <button class="metric" type="button" :class="{ active: query.reminderStatus === 'all' }" @click="selectStatus('all')">
          <span>全部待办</span><b>{{ summary.totalCount }}</b><small>动态实时计算</small>
        </button>
      </section>

      <section class="erp-search-card">
        <a-form layout="inline"><a-row :gutter="24">
          <a-col :md="6" :sm="24"><a-form-item label="关键字"><a-input v-model.trim="query.keyword" allow-clear placeholder="编号、名称、联系人或手机号" @pressEnter="search" /></a-form-item></a-col>
          <a-col :md="5" :sm="24"><a-form-item label="对象类型"><a-select v-model="query.objectType" allow-clear placeholder="全部"><a-select-option value="lead">线索</a-select-option><a-select-option value="customer">客户</a-select-option></a-select></a-form-item></a-col>
          <a-col :md="5" :sm="24"><a-form-item label="待办状态"><a-select v-model="query.reminderStatus"><a-select-option value="all">全部待办</a-select-option><a-select-option value="overdue">已逾期</a-select-option><a-select-option value="due_today">今日到期</a-select-option></a-select></a-form-item></a-col>
          <a-col :md="24" :sm="24" class="erp-search-actions"><a-button type="primary" icon="search" @click="search">查询</a-button><a-button icon="redo" @click="resetQuery">重置</a-button></a-col>
        </a-row></a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div><h3>待跟进事项</h3><p>新增有效跟进并设置下一次时间后，本待办将自动更新；转派或进入公海后自动从个人待办移除。</p></div>
          <div class="erp-toolbar-actions"><span class="erp-record-count">共 {{ total }} 条</span><a-button icon="reload" :loading="loading" @click="refreshAll">刷新</a-button><a-button type="primary" icon="plus" @click="createFollowUp()">新增跟进</a-button></div>
        </div>
        <a-table
          :row-key="rowKey"
          bordered
          size="middle"
          :columns="columns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :scroll="{ x: 1120 }"
          :locale="{ emptyText: '当前没有到期或逾期的跟进事项' }"
          @change="changePage">
          <template slot="status" slot-scope="text, record"><a-tag :color="record.reminderStatus === 'overdue' ? 'red' : 'orange'">{{ record.reminderStatus === 'overdue' ? '已逾期' : '今日到期' }}</a-tag></template>
          <template slot="objectType" slot-scope="text"><a-tag :color="text === 'lead' ? 'blue' : 'purple'">{{ text === 'lead' ? '线索' : '客户' }}</a-tag></template>
          <template slot="object" slot-scope="text, record"><div class="object-cell"><a @click="openObject(record)">{{ record.objectName || '—' }}</a><small>{{ record.objectCode || '—' }}</small></div></template>
          <template slot="nextTime" slot-scope="text, record"><div class="time-cell" :class="record.reminderStatus"><b>{{ formatTime(record.nextFollowUpTime) }}</b><small>{{ delayLabel(record) }}</small></div></template>
          <template slot="lastTime" slot-scope="text">{{ formatTime(text) }}</template>
          <template slot="contact" slot-scope="text, record"><div>{{ record.contactName || '—' }}</div><small class="muted">{{ maskMobile(record.mobile) }}</small></template>
          <template slot="operation" slot-scope="text, record"><a @click="openObject(record)">查看档案</a><a-divider type="vertical" /><a @click="createFollowUp(record)">新增跟进</a></template>
        </a-table>
      </section>
    </div>
  </page-header-wrapper>
</template>

<script>
import { pageCrmReminder, getCrmReminderSummary } from '@/api/erp/crm'

export default {
  name: 'ErpCrmReminder',
  data () {
    return {
      query: { reminderStatus: 'all', objectType: undefined, keyword: '' },
      summary: { overdueCount: 0, dueTodayCount: 0, totalCount: 0 },
      rows: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
      loading: false,
      columns: [
        { title: '状态', key: 'status', width: 100, fixed: 'left', scopedSlots: { customRender: 'status' } },
        { title: '类型', dataIndex: 'objectType', width: 90, scopedSlots: { customRender: 'objectType' } },
        { title: '跟进对象', key: 'object', width: 230, scopedSlots: { customRender: 'object' } },
        { title: '联系人 / 手机号', key: 'contact', width: 155, scopedSlots: { customRender: 'contact' } },
        { title: '下次跟进时间', dataIndex: 'nextFollowUpTime', width: 190, scopedSlots: { customRender: 'nextTime' } },
        { title: '最近跟进时间', dataIndex: 'lastFollowUpTime', width: 165, scopedSlots: { customRender: 'lastTime' } },
        { title: '操作', key: 'operation', width: 180, fixed: 'right', scopedSlots: { customRender: 'operation' } }
      ]
    }
  },
  computed: {
    pagination () { return { current: this.pageNum, pageSize: this.pageSize, total: this.total, showSizeChanger: true, showTotal: total => `共 ${total} 条` } }
  },
  created () {
    if (this.$route.query.objectType) this.query.objectType = this.$route.query.objectType
    if (this.$route.query.keyword) this.query.keyword = this.$route.query.keyword
    this.refreshAll()
  },
  methods: {
    rowKey (record) { return `${record.objectType}:${record.objectId}` },
    refreshAll () { this.loadSummary(); this.loadRows() },
    loadSummary () { return getCrmReminderSummary(5).then(res => { this.summary = res.data || this.summary }) },
    loadRows () {
      this.loading = true
      return pageCrmReminder({ ...this.query, pageNum: this.pageNum, pageSize: this.pageSize }).then(res => {
        this.rows = (res.data && res.data.records) || []
        this.total = Number((res.data && res.data.total) || 0)
      }).finally(() => { this.loading = false })
    },
    search () { this.pageNum = 1; this.loadRows() },
    resetQuery () { this.query = { reminderStatus: 'all', objectType: undefined, keyword: '' }; this.pageNum = 1; this.refreshAll() },
    selectStatus (status) { this.query.reminderStatus = status; this.search() },
    changePage (page) { this.pageNum = page.current; this.pageSize = page.pageSize; this.loadRows() },
    openObject (record) { this.$router.push(record.objectType === 'lead' ? '/erp/crm/lead' : '/erp/customer') },
    createFollowUp (record) {
      const hasTarget = record && record.objectId != null && ['lead', 'customer'].includes(record.objectType)
      const query = hasTarget
        ? { targetType: record.objectType, targetId: String(record.objectId) }
        : { action: 'create' }
      this.$router.push({ path: '/erp/crm/follow-up', query })
    },
    formatTime (value) { return value ? String(value).replace('T', ' ').slice(0, 16) : '—' },
    maskMobile (value) { const text = String(value || ''); return text.length >= 7 ? text.slice(0, 3) + '****' + text.slice(-4) : (text || '—') },
    delayLabel (record) {
      if (record.reminderStatus !== 'overdue') return '今日待完成'
      const minutes = Number(record.overdueMinutes || 0)
      if (minutes >= 1440) return `已逾期 ${Math.floor(minutes / 1440)} 天`
      if (minutes >= 60) return `已逾期 ${Math.floor(minutes / 60)} 小时`
      return `已逾期 ${Math.max(minutes, 1)} 分钟`
    }
  }
}
</script>

<style lang="less" scoped>
.crm-reminder-page { padding-bottom: 24px; }
.reminder-overview { display: grid; grid-template-columns: minmax(320px, 1fr) repeat(3, 170px); gap: 12px; align-items: stretch; margin-bottom: 16px; }
.overview-copy, .metric { border: 1px solid #e4e9f0; background: #fff; border-radius: 8px; }
.overview-copy { padding: 20px 24px; }
.overview-kicker { color: #1677ff; font-size: 12px; font-weight: 600; letter-spacing: .08em; }
.overview-copy h2 { margin: 5px 0 4px; font-size: 22px; color: #17233d; }
.overview-copy p { margin: 0; color: #738099; }
.metric { appearance: none; text-align: left; padding: 15px 18px; cursor: pointer; transition: border-color .18s, box-shadow .18s; color: #5a667a; }
.metric:hover, .metric.active { border-color: #91caff; box-shadow: 0 2px 10px rgba(22, 119, 255, .1); }
.metric b { display: block; margin: 5px 0 2px; font-size: 25px; line-height: 1; color: #17233d; }
.metric small { color: #9aa5b5; }
.metric-danger b { color: #cf1322; }
.metric-warning b { color: #d46b08; }
.object-cell, .time-cell { display: flex; flex-direction: column; gap: 2px; }
.object-cell small, .time-cell small, .muted { color: #98a2b3; font-size: 12px; }
.time-cell.overdue b { color: #cf1322; }
.time-cell.due_today b { color: #d46b08; }
@media (max-width: 1100px) { .reminder-overview { grid-template-columns: 1fr repeat(3, 145px); } }
@media (max-width: 820px) { .reminder-overview { grid-template-columns: 1fr 1fr; } .overview-copy { grid-column: 1 / -1; } }
</style>
